import { describeSeatLayout, resolveSelectedSeatReferences } from './seatPreferenceMetadata';
import { PASSENGER_COUNTS } from './seatPreferenceConstants';
import { writeSeatPreferences } from './seatPreferencesStorage';
import { getSeatCategory, isSeat } from './seats';
import { isPrimarySeatReference } from './seatReferenceTransport';

/**
 * Saves a complete primary-seat choice at an explicit user confirmation boundary.
 * Invalid, incomplete, ambiguous, or inaccessible data is a fail-open no-op.
 * @param {object} params Confirmation data.
 * @param {Array} params.layout Seat layout used to make the selection.
 * @param {Array<object>} params.selectedSeats Final ordered selected seat objects.
 * @param {number} params.passengerCount Authoritative booking passenger count.
 * @param {string} params.tripSlug Trip identity used by the layout descriptor.
 * @param {string} [params.diagramType] Diagram family. Omitted values remain unknown.
 * @param {boolean} [params.showUniformLayout=false] Whether uniform rendering is active.
 * @param {Storage} [params.storage] Optional storage implementation for tests.
 * @returns {boolean} Whether the preference bucket was written.
 */
export default function saveConfirmedSeatPreference({
  layout,
  selectedSeats,
  passengerCount,
  tripSlug,
  diagramType,
  showUniformLayout = false,
  storage,
} = {}) {
  try {
    if (!PASSENGER_COUNTS.includes(passengerCount) || !Array.isArray(selectedSeats) || !tripSlug) {
      return false;
    }

    const primarySeats = selectedSeats.filter(isPrimarySeatReference);
    if (primarySeats.length !== passengerCount) return false;

    const descriptors = describeSeatLayout(layout, {
      tripSlug,
      diagramType,
      showUniformLayout,
    });
    const resolved = resolveSelectedSeatReferences(descriptors, primarySeats);
    if (!resolved || resolved.length !== passengerCount) return false;
    if (resolved.some(({ seat }) => !isSeat(getSeatCategory(seat)))) return false;

    const orderedPreferences = resolved.map(({ preference }) => ({
      ...preference,
      position: preference.position ? [...preference.position] : null,
    }));

    return writeSeatPreferences(passengerCount, orderedPreferences, storage);
  } catch (_error) {
    return false;
  }
}
